/**
 * SkiSupply — scroll reveal orchestration.
 *
 * Content rises and fades as it enters the viewport, staggered in reading
 * order so a section assembles itself rather than appearing all at once.
 *
 * Rules this follows, and why:
 *
 *   Reveals fire once. Re-animating on every scroll-up turns a page into a
 *   slot machine and makes it tiring to re-read. Each element is unobserved
 *   the moment it has played.
 *
 *   Content is visible by default. The hidden start state lives behind the
 *   `ss-reveal` class on <html>, which this script adds. If it never runs, or
 *   IntersectionObserver is missing, everything renders normally — a reveal
 *   system that can hide content permanently is a bug, not a feature.
 *
 *   Anything already on screen at load is shown immediately, without a
 *   reveal. Animating what the visitor is already looking at is a delay, not
 *   an effect.
 *
 *   Nested targets are collapsed: if an ancestor is already revealing, the
 *   descendant is skipped so nothing double-animates.
 *
 * Motion is opacity and transform only, so reveals stay on the compositor.
 */

(() => {
  'use strict';

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const STAGGER_MS = 55;
  const MAX_STAGGER_STEPS = 9; // beyond this the last items feel abandoned
  const ROOT_MARGIN = '0px 0px -12% 0px'; // start just before it reaches the fold

  /* Block-level content worth revealing. Deliberately excluded: prices, stock
     and inventory messages, validation and error text, cart totals, and the
     checkout button — see the note in skisupply.css. */
  const CANDIDATES = [
    'h1',
    'h2',
    '.text-block',
    '.button',
    '.product-card',
    '.collection-card',
    '.group-block',
    '[data-ss-reveal]',
  ].join(', ');

  const root = document.querySelector('main') || document.body;

  /* Stagger is assigned at the moment of entry, not at page load. Baking a
     delay into an element up front means a card sitting eighth in the DOM
     always waits its eighth of a second — even when it is the first thing the
     visitor scrolls to. Batching by arrival keeps the cascade tied to what is
     actually coming into view. Order within a batch is visual (top, then
     left), not DOM order, so a grid ripples the way it reads. */
  const observer = new IntersectionObserver(
    (entries) => {
      const arriving = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => entry.target)
        .sort((a, b) => {
          const boxA = a.getBoundingClientRect();
          const boxB = b.getBoundingClientRect();
          return boxA.top - boxB.top || boxA.left - boxB.left;
        });

      arriving.forEach((el, index) => {
        el.style.setProperty(
          '--ss-reveal-delay',
          `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS}ms`
        );
        el.classList.add('ss-revealed');
        observer.unobserve(el);
      });
    },
    { rootMargin: ROOT_MARGIN, threshold: 0.01 }
  );

  function prepare(scope) {
    const sections = scope.querySelectorAll('.shopify-section');
    (sections.length ? sections : [scope]).forEach((section) => {
      const found = [...section.querySelectorAll(CANDIDATES)];
      const chosen = [];

      found.forEach((el) => {
        // Skip anything already covered by a chosen ancestor.
        if (chosen.some((picked) => picked !== el && picked.contains(el))) return;
        if (el.dataset.ssRevealBound) return;
        chosen.push(el);
      });

      chosen.forEach((el) => {
        el.dataset.ssRevealBound = '1';

        // Already in view on load: show it outright, no animation, no delay.
        const box = el.getBoundingClientRect();
        if (box.top < window.innerHeight && box.bottom > 0) {
          el.classList.add('ss-revealed', 'ss-reveal-instant');
          return;
        }

        observer.observe(el);
      });
    });
  }

  document.documentElement.classList.add('ss-reveal');
  prepare(root);

  /* Sections re-render on filtering, pagination and theme-editor edits.
     Re-scan so new content participates instead of arriving pre-hidden. */
  const rescan = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) prepare(node);
      });
    });
  });
  rescan.observe(root, { childList: true, subtree: true });

  // Theme editor: re-scan whenever a section is rebuilt.
  document.addEventListener('shopify:section:load', (event) => prepare(event.target));
})();

/**
 * SkiSupply — announcement bar marquee.
 *
 * The message repeats and travels left to right with no seam.
 *
 * How the loop is made seamless: the track is filled with identical copies and
 * animated by exactly ONE copy's width, then snapped back. Because the content
 * repeats at precisely that interval, the snap is invisible — the strip appears
 * to run forever. Shifting by a percentage of the whole track instead would
 * make correctness depend on the copies dividing evenly into it, which is
 * fragile; one copy width is self-correcting.
 *
 * Two measurement traps this avoids, both of which produced visible gaps:
 *
 *   The slides start life as stretched grid items, so measuring one before the
 *   marquee layout is applied returns the width of the whole bar rather than of
 *   the text. The layout class goes on FIRST, then measurement happens.
 *
 *   Web fonts change text width when they land. Measuring beforehand sizes the
 *   strip against fallback metrics, so it waits for document.fonts.ready and
 *   rebuilds on resize.
 *
 * Speed is pixels per second rather than a loop duration, so the strip travels
 * at the same visual pace no matter how long the message is.
 *
 * Accessibility: WCAG 2.2.2 requires a way to stop content that moves for more
 * than five seconds. Reduced motion stops it outright, and hover or keyboard
 * focus pauses it. Only the original copy is exposed to screen readers; every
 * clone is aria-hidden, so the message is announced once, not many times.
 */

(() => {
  'use strict';

  const bar = document.querySelector('.announcement-bar[data-ss-anim="marquee"]');
  if (!bar) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const slider = bar.querySelector('.announcement-bar__slider');
  const track = bar.querySelector('.announcement-bar__slides');
  if (!slider || !track || !track.children.length) return;

  const pixelsPerSecond = Number(bar.dataset.ssSpeed) || 90;
  const pristine = [...track.children].map((node) => node.cloneNode(true));

  function build() {
    // Start from one clean copy, with the marquee layout already applied so the
    // measurement below sees flex-sized text, not stretched grid cells.
    track.replaceChildren(...pristine.map((node) => node.cloneNode(true)));
    bar.dataset.ssMarqueeReady = '1';

    const unitWidth = [...track.children].reduce(
      (total, el) => total + el.getBoundingClientRect().width,
      0
    );
    const barWidth = slider.getBoundingClientRect().width;
    if (!unitWidth || !barWidth) return;

    // Enough copies that the strip still covers the bar at full shift, plus one
    // spare so nothing is ever exposed at the trailing edge.
    const copies = Math.ceil((barWidth + unitWidth) / unitWidth) + 1;

    const fill = document.createDocumentFragment();
    for (let i = 1; i < copies; i++) {
      pristine.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('ref');
        fill.appendChild(clone);
      });
    }
    track.appendChild(fill);

    track.style.setProperty('--ss-marquee-shift', `${unitWidth}px`);
    track.style.animationDuration = `${(unitWidth / pixelsPerSecond).toFixed(3)}s`;
  }

  const run = () => {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(build);
    } else {
      build();
    }
  };

  run();

  let resizeTimer = null;
  addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(build, 200);
  });
})();
